// @flow
import React, { Component } from 'react';
import ReactTooltip from 'react-tooltip';
import PageTemplate from 'components/base/PageTemplate';
import MainMobileHeadContainer from 'containers/main/MainMobileHeadContainer';
import MainHeadContainer from 'containers/main/MainHeadContainer';
import { type ContextRouter } from 'react-router-dom';
import { CartActions, BaseActions } from 'store/actionCreators';
import './Chemical.scss';

const MEILISEARCH_URL = 'https://ms-ai.cacheby.com';
const MEILISEARCH_KEY = '8b302632c257a5524b97753d6abe0d731a802fc55500080145e1f7f44551f197';

type UploadedItem = {
  rowIndex: number,
  casNumber: string,
  productName: ?string,
  brand: ?string,
  unit: ?string,
  quantity: ?number,
  currentPrice: ?number,
};

type ProductPrice = {
  id: string,
  unit: string,
  brand: string,
  price: number,
  status: string,
  cas_number: ?string,
  description: string,
  part_number: string,
};

type MatchedProduct = {
  id: string,
  title: string,
  url_slug: string,
  thumbnail: string,
  username: string,
  brand: string,
  price: number,
  min_price: number,
  max_price: number,
  cas_numbers: Array<string>,
  product_prices: Array<ProductPrice>,
};

type SearchResult = {
  ...UploadedItem,
  status: 'pending' | 'found' | 'not_found' | 'multiple',
  matchedProducts: Array<MatchedProduct>,
  bestMatch: ?MatchedProduct,
  confidence: number,
  priceDiff: ?number,
  priceDiffPercent: ?number,
  isExpanded: boolean,
};

type State = {
  uploadedData: Array<UploadedItem>,
  searchResults: Array<SearchResult>,
  isProcessing: boolean,
  processingProgress: number,
  searchStartTime: ?number,
  totalSearchTime: ?number,
  isDragging: boolean,
  error: ?string,
  pageVisitTracked: boolean,
  searchQuery: string,
};

type Props = ContextRouter;

const validateCasNumber = (cas: string): boolean => {
  if (!cas) return false;
  const cleaned = cas.toString().trim();
  return /^\d{2,7}-\d{2}-\d$/.test(cleaned);
};

const formatPrice = (price: ?number): string => {
  if (!price || price <= 0) return '-';
  return `${Number(price).toLocaleString()}원`;
};

const formatPriceDiff = (diff: ?number, percent: ?number): { text: string, className: string } => {
  if (diff === null || diff === undefined) return { text: '-', className: '' };
  if (diff < 0) {
    return {
      text: `${Math.abs(diff).toLocaleString()}원 절감 (${Math.abs(percent || 0).toFixed(1)}%)`,
      className: 'price-savings',
    };
  }
  if (diff > 0) {
    return {
      text: `${diff.toLocaleString()}원 증가 (${(percent || 0).toFixed(1)}%)`,
      className: 'price-increase',
    };
  }
  return { text: '동일', className: 'price-same' };
};

class Chemical extends Component<Props, State> {
  fileInputRef: { current: null | HTMLInputElement };

  constructor(props: Props) {
    super(props);
    this.state = {
      uploadedData: [],
      searchResults: [],
      isProcessing: false,
      processingProgress: 0,
      searchStartTime: null,
      totalSearchTime: null,
      isDragging: false,
      error: null,
      pageVisitTracked: false,
      searchQuery: '',
    };
    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    this.trackEvent('chemical_page_visit', {});
    this.loadSavedResults();
    ReactTooltip.rebuild();
  }

  componentDidUpdate() {
    ReactTooltip.rebuild();
  }

  loadSavedResults = () => {
    try {
      const saved = localStorage.getItem('chemical_search_results');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.setState({ searchResults: parsed });
      }
    } catch (e) {
      console.error('Failed to load saved results:', e);
    }
  };

  saveResults = (results: Array<SearchResult>) => {
    try {
      localStorage.setItem('chemical_search_results', JSON.stringify(results));
    } catch (e) {
      console.error('Failed to save results:', e);
    }
  };

  clearSavedResults = () => {
    try {
      localStorage.removeItem('chemical_search_results');
      this.setState({ searchResults: [], uploadedData: [] });
    } catch (e) {
      console.error('Failed to clear results:', e);
    }
  };

  trackEvent = (eventName: string, properties: Object) => {
    const timestamp = Date.now();
    const eventData = {
      event: eventName,
      timestamp,
      ...properties,
    };
    console.log('[Chemical Tracking]', eventData);

    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, properties);
    }
  };

  handleDragOver = (e: SyntheticDragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: true });
  };

  handleDragLeave = (e: SyntheticDragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });
  };

  handleDrop = (e: SyntheticDragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });

    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      this.processExcelFile(files[0]);
    }
  };

  handleFileSelect = (e: SyntheticInputEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      this.processExcelFile(files[0]);
    }
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = '';
    }
  };

  handleSearchQueryChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
    this.setState({ searchQuery: e.target.value });
  };

  handleSearchSubmit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { searchQuery, searchResults: existingResults } = this.state;
    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery) {
      this.setState({ error: 'CAS 번호를 입력해주세요.' });
      return;
    }

    this.trackEvent('chemical_search_start', { query: trimmedQuery, type: 'direct' });

    const casNumbers = trimmedQuery.split(/[,\s\n]+/).filter(cas => cas.trim());
    const existingCasNumbers = new Set(existingResults.map(r => r.casNumber));
    const newCasNumbers = casNumbers.filter(cas => !existingCasNumbers.has(cas.trim()));

    if (newCasNumbers.length === 0) {
      this.setState({ error: '이미 검색된 CAS 번호입니다.', searchQuery: '' });
      return;
    }

    const uploadedItems: Array<UploadedItem> = newCasNumbers.map((cas, idx) => ({
      rowIndex: existingResults.length + idx + 1,
      casNumber: cas.trim(),
      productName: null,
      brand: null,
      unit: null,
      quantity: null,
      currentPrice: null,
    }));

    const pendingResults = uploadedItems.map(item => ({
      ...item,
      status: 'pending',
      matchedProducts: [],
      bestMatch: null,
      confidence: 0,
      priceDiff: null,
      priceDiffPercent: null,
      isExpanded: false,
    }));

    this.setState({
      isProcessing: true,
      processingProgress: 0,
      error: null,
      searchStartTime: Date.now(),
      uploadedData: uploadedItems,
      searchResults: [...existingResults, ...pendingResults],
    });

    const searchStartTime = Date.now();
    const results: Array<SearchResult> = await Promise.all(
      uploadedItems.map(async item => {
        const searchResult = await this.searchByCasNumber(item.casNumber);
        return this.processSearchResult(item, searchResult);
      }),
    );

    const totalSearchTime = Date.now() - searchStartTime;
    const matchedCount = results.filter(r => r.status === 'found' || r.status === 'multiple').length;

    this.trackEvent('chemical_search_complete', {
      query: trimmedQuery,
      type: 'direct',
      totalItems: uploadedItems.length,
      matchedItems: matchedCount,
      processingTimeMs: totalSearchTime,
    });

    const mergedResults = [...existingResults, ...results];

    this.setState({
      isProcessing: false,
      processingProgress: 100,
      searchResults: mergedResults,
      totalSearchTime,
      searchQuery: '',
    });

    this.saveResults(mergedResults);
  };

  processExcelFile = async (file: File) => {
    const isExcel =
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.type === 'text/csv' ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls') ||
      file.name.endsWith('.csv');

    if (!isExcel) {
      this.setState({ error: '엑셀 파일(.xlsx, .xls) 또는 CSV 파일만 업로드 가능합니다.' });
      return;
    }

    this.trackEvent('chemical_upload_start', { fileName: file.name, fileSize: file.size });
    const uploadStartTime = Date.now();

    try {
      const xlsxModule = await import(/* webpackChunkName: "xlsx" */ 'xlsx/dist/xlsx.full.min.js');
      const XLSX = xlsxModule.default || xlsxModule;

      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (jsonData.length < 2) {
        this.setState({ error: '데이터가 없습니다. 헤더와 최소 1개의 데이터 행이 필요합니다.' });
        return;
      }

      const headers = jsonData[0].map((h: any) => String(h).toLowerCase().trim());
      const casIndex = headers.findIndex((h: string) =>
        h.includes('cas') || h.includes('번호') || h.includes('number'),
      );

      if (casIndex === -1) {
        this.setState({ error: 'CAS Number 컬럼을 찾을 수 없습니다. "CAS Number" 또는 "CAS 번호" 컬럼이 필요합니다.' });
        return;
      }

      const nameIndex = headers.findIndex((h: string) =>
        h.includes('product') || h.includes('name') || h.includes('제품') || h.includes('이름'),
      );
      const brandIndex = headers.findIndex((h: string) =>
        h.includes('brand') || h.includes('브랜드') || h.includes('제조사'),
      );
      const unitIndex = headers.findIndex((h: string) =>
        h.includes('unit') || h.includes('단위') || h.includes('규격'),
      );
      const quantityIndex = headers.findIndex((h: string) =>
        h.includes('quantity') || h.includes('수량') || h.includes('qty'),
      );
      const priceIndex = headers.findIndex((h: string) =>
        h.includes('price') || h.includes('가격') || h.includes('단가'),
      );

      const uploadedItems: Array<UploadedItem> = [];
      const errors: Array<string> = [];

      jsonData.slice(1).forEach((row, idx) => {
        if (!row || row.length === 0) return;

        const casNumber = row[casIndex] ? String(row[casIndex]).trim() : '';
        if (!casNumber) return;

        if (!validateCasNumber(casNumber)) {
          errors.push(`행 ${idx + 2}: 유효하지 않은 CAS 번호 형식 (${casNumber})`);
        }

        uploadedItems.push({
          rowIndex: idx + 1,
          casNumber,
          productName: nameIndex >= 0 && row[nameIndex] ? String(row[nameIndex]).trim() : null,
          brand: brandIndex >= 0 && row[brandIndex] ? String(row[brandIndex]).trim() : null,
          unit: unitIndex >= 0 && row[unitIndex] ? String(row[unitIndex]).trim() : null,
          quantity: quantityIndex >= 0 && row[quantityIndex] ? Number(row[quantityIndex]) : null,
          currentPrice: priceIndex >= 0 && row[priceIndex] ? Number(row[priceIndex]) : null,
        });
      });

      if (uploadedItems.length === 0) {
        this.setState({ error: '유효한 CAS 번호를 가진 데이터가 없습니다.' });
        return;
      }

      const uploadEndTime = Date.now();
      const uploadDuration = uploadEndTime - uploadStartTime;
      const fileSizeMB = file.size / (1024 * 1024);
      const processingSpeed = fileSizeMB / (uploadDuration / 1000);

      this.trackEvent('chemical_upload_complete', {
        fileName: file.name,
        fileSize: file.size,
        rowCount: uploadedItems.length,
        processingTimeMs: uploadDuration,
        processingSpeedMBps: processingSpeed.toFixed(2),
        validationErrors: errors.length,
      });

      this.setState(
        {
          uploadedData: uploadedItems,
          error: errors.length > 0 ? `${errors.length}개의 경고: ${errors.slice(0, 3).join(', ')}` : null,
        },
        () => {
          this.performBatchSearch();
        },
      );
    } catch (err) {
      console.error('Excel parsing error:', err);
      this.setState({ error: '엑셀 파일을 읽는 중 오류가 발생했습니다.' });
    }
  };

  performBatchSearch = async () => {
    const { uploadedData, searchResults: existingResults } = this.state;
    if (uploadedData.length === 0) return;

    const existingCasNumbers = new Set(existingResults.map(r => r.casNumber));
    const newItems = uploadedData.filter(item => !existingCasNumbers.has(item.casNumber));

    if (newItems.length === 0) {
      this.setState({ error: '모든 CAS 번호가 이미 검색되었습니다.' });
      return;
    }

    const pendingResults = newItems.map(item => ({
      ...item,
      status: 'pending',
      matchedProducts: [],
      bestMatch: null,
      confidence: 0,
      priceDiff: null,
      priceDiffPercent: null,
      isExpanded: false,
    }));

    const searchStartTime = Date.now();
    this.setState({
      isProcessing: true,
      processingProgress: 0,
      searchStartTime,
      searchResults: [...existingResults, ...pendingResults],
    });

    this.trackEvent('chemical_search_start', { totalItems: newItems.length });

    const results: Array<SearchResult> = await Promise.all(
      newItems.map(async item => {
        const searchResult = await this.searchByCasNumber(item.casNumber);
        return this.processSearchResult(item, searchResult);
      }),
    );

    const totalSearchTime = Date.now() - searchStartTime;
    const matchedCount = results.filter(r => r.status === 'found' || r.status === 'multiple').length;
    const notFoundCount = results.filter(r => r.status === 'not_found').length;

    this.trackEvent('chemical_search_complete', {
      totalItems: newItems.length,
      matchedItems: matchedCount,
      notFoundItems: notFoundCount,
      processingTimeMs: totalSearchTime,
      matchRate: ((matchedCount / newItems.length) * 100).toFixed(2),
    });

    const mergedResults = [...existingResults, ...results];

    this.setState({
      isProcessing: false,
      processingProgress: 100,
      searchResults: mergedResults,
      totalSearchTime,
    });

    this.saveResults(mergedResults);
  };

  searchByCasNumber = async (casNumber: string): Promise<Array<MatchedProduct>> => {
    try {
      const response = await fetch(`${MEILISEARCH_URL}/indexes/products/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${MEILISEARCH_KEY}`,
        },
        body: JSON.stringify({
          q: casNumber,
          limit: 50,
          filter: `cas_numbers = "${casNumber}"`,
        }),
      });

      const data = await response.json();
      return data.hits || [];
    } catch (error) {
      console.error('CAS number search error:', error);
      return [];
    }
  };

  processSearchResult = (item: UploadedItem, matches: Array<MatchedProduct>): SearchResult => {
    if (matches.length === 0) {
      return {
        ...item,
        status: 'not_found',
        matchedProducts: [],
        bestMatch: null,
        confidence: 0,
        priceDiff: null,
        priceDiffPercent: null,
        isExpanded: false,
      };
    }

    const sortedMatches = [...matches].sort((a, b) => (a.min_price || 0) - (b.min_price || 0));
    const bestMatch = sortedMatches[0];

    let priceDiff = null;
    let priceDiffPercent = null;
    if (item.currentPrice && bestMatch.min_price) {
      priceDiff = bestMatch.min_price - item.currentPrice;
      priceDiffPercent = (priceDiff / item.currentPrice) * 100;
    }

    const confidence = 100;

    return {
      ...item,
      status: matches.length === 1 ? 'found' : 'multiple',
      matchedProducts: sortedMatches,
      bestMatch,
      confidence,
      priceDiff,
      priceDiffPercent,
      isExpanded: false,
    };
  };

  toggleRowExpansion = (index: number) => {
    this.setState(prevState => {
      const newResults = [...prevState.searchResults];
      newResults[index] = {
        ...newResults[index],
        isExpanded: !newResults[index].isExpanded,
      };
      return { searchResults: newResults };
    });

    const result = this.state.searchResults[index];
    if (!result.isExpanded) {
      this.trackEvent('chemical_substitute_click', {
        casNumber: result.casNumber,
        substituteCount: result.matchedProducts.length,
        rankPosition: index + 1,
      });
    }
  };

  handleDeleteRow = (index: number) => {
    const { searchResults } = this.state;
    const newResults = searchResults.filter((_, i) => i !== index);
    this.setState({ searchResults: newResults });
    this.saveResults(newResults);
  };

  handleProductClick = (result: SearchResult, product: MatchedProduct, rankPosition: number) => {
    this.trackEvent('chemical_result_click', {
      casNumber: result.casNumber,
      productId: product.id,
      productTitle: product.title,
      brand: product.brand,
      minPrice: product.min_price,
      rankPosition,
      isSubstitute: result.matchedProducts.length > 1,
    });
  };

  handleProductSelect = (result: SearchResult, product: MatchedProduct, rankPosition: number) => {
    window.open(`/@${product.username}/products/${product.id}`, '_blank');
    this.trackEvent('chemical_product_select', {
      casNumber: result.casNumber,
      selectedProductId: product.id,
      productTitle: product.title,
      brand: product.brand,
      minPrice: product.min_price,
      rankPosition,
      totalOptions: result.matchedProducts.length,
      isSubstitute: result.matchedProducts.length > 1,
    });
  };

  downloadTemplate = () => {
    const templateData = [
      ['CAS Number', 'Product Name', 'Brand', 'Unit', 'Quantity', 'Current Price'],
      ['7732-18-5', 'Water', 'Fisher', '1L', 10, 50000],
      ['64-17-5', 'Ethanol', 'Sigma', '500ml', 5, 80000],
      ['67-56-1', 'Methanol', 'Merck', '1L', 3, 75000],
    ];

    const csvContent = templateData.map(row => row.join(',')).join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'chemical_search_template.csv';
    link.click();

    this.trackEvent('chemical_template_download', {});
  };

  handleAddToCart = async (product: MatchedProduct, price: ProductPrice) => {
    try {
      await CartActions.addItemCart({
        productId: product.id,
        productPriceId: price.id,
        quantity: 1,
      });
      await CartActions.getCart();

      BaseActions.showToast({
        type: 'success',
        message: '장바구니에 담았습니다.',
      });

      this.trackEvent('chemical_add_to_cart', {
        productId: product.id,
        productTitle: product.title,
        priceId: price.id,
        partNumber: price.part_number,
        brand: price.brand,
        price: price.price,
      });
    } catch (e) {
      console.error('장바구니 담기 실패:', e);
      BaseActions.showToast({
        type: 'error',
        message: '장바구니 담기에 실패했습니다. 로그인이 필요할 수 있습니다.',
      });
    }
  };

  exportResults = () => {
    const { searchResults } = this.state;
    if (searchResults.length === 0) return;

    const exportData = [
      [
        '검색 CAS Number',
        '검색 제품명',
        '검색 브랜드',
        '검색 현재가격',
        '검색 상태',
        '매칭 상품명',
        '매칭 상품 브랜드',
        '매칭 상품 최저가',
        '매칭 상품 최고가',
        '매칭 상품 URL',
        '옵션 Part Number',
        '옵션 브랜드',
        '옵션 설명',
        '옵션 단위',
        '옵션 가격',
        '옵션 CAS Number',
        '가격차이',
        '가격차이(%)',
      ],
    ];

    searchResults.forEach(result => {
      if (result.matchedProducts.length === 0) {
        exportData.push([
          result.casNumber,
          result.productName || '',
          result.brand || '',
          result.currentPrice || '',
          '미매칭',
          '', '', '', '', '', '', '', '', '', '', '', '', '',
        ]);
      } else {
        result.matchedProducts.forEach(product => {
          if (product.product_prices && product.product_prices.length > 0) {
            product.product_prices.forEach(price => {
              const priceDiff = result.currentPrice && price.price
                ? price.price - result.currentPrice
                : '';
              const priceDiffPercent = result.currentPrice && price.price
                ? ((price.price - result.currentPrice) / result.currentPrice * 100).toFixed(2)
                : '';

              exportData.push([
                result.casNumber,
                result.productName || '',
                result.brand || '',
                result.currentPrice || '',
                '매칭됨',
                product.title || '',
                product.brand || '',
                product.min_price || '',
                product.max_price || '',
                `/@${product.username}/products/${product.id}`,
                price.part_number || '',
                price.brand || '',
                price.description || '',
                price.unit || '',
                price.price || '',
                price.cas_number || '',
                priceDiff,
                priceDiffPercent,
              ]);
            });
          } else {
            exportData.push([
              result.casNumber,
              result.productName || '',
              result.brand || '',
              result.currentPrice || '',
              '매칭됨',
              product.title || '',
              product.brand || '',
              product.min_price || '',
              product.max_price || '',
              `/@${product.username}/products/${product.id}`,
              '', '', '', '', '', '',
              result.priceDiff || '',
              result.priceDiffPercent ? result.priceDiffPercent.toFixed(2) : '',
            ]);
          }
        });
      }
    });

    const csvContent = exportData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `chemical_search_results_${Date.now()}.csv`;
    link.click();

    this.trackEvent('chemical_export_complete', {
      totalRows: searchResults.length,
      matchedRows: searchResults.filter(r => r.status !== 'not_found').length,
    });
  };

  renderSearchArea = () => {
    const { searchQuery, isProcessing, isDragging } = this.state;

    return (
      <div
        className={`chemical-search-area ${isDragging ? 'dragging' : ''}`}
        onDragOver={this.handleDragOver}
        onDragLeave={this.handleDragLeave}
        onDrop={this.handleDrop}
      >
        <form onSubmit={this.handleSearchSubmit} className="search-form">
          <input
            type="text"
            className="search-input"
            placeholder="CAS 번호, 제품명, 브랜드로 검색..."
            value={searchQuery}
            onChange={this.handleSearchQueryChange}
            disabled={isProcessing}
          />
          <input
            ref={this.fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={this.handleFileSelect}
            style={{ display: 'none' }}
            disabled={isProcessing}
          />
          <button type="submit" className="search-btn" disabled={isProcessing}>
            {isProcessing ? '검색 중...' : '검색'}
          </button>
        </form>
        <div className="search-helper">
          <span className="helper-hint">엑셀 파일을 여기에 드래그하거나</span>
          <button
            type="button"
            className="helper-link"
            onClick={() => !isProcessing && this.fileInputRef.current && this.fileInputRef.current.click()}
            disabled={isProcessing}
            data-tip="CAS 번호가 포함된 엑셀/CSV 파일을 업로드하면 대량 검색이 가능합니다"
            data-for="chemical-tooltip"
          >
            파일 선택
          </button>
          <span className="helper-divider">|</span>
          <button
            type="button"
            className="helper-link"
            onClick={this.downloadTemplate}
            disabled={isProcessing}
            data-tip="CAS 번호, 제품명, 브랜드, 가격 컬럼이 포함된 샘플 템플릿입니다"
            data-for="chemical-tooltip"
          >
            템플릿 다운로드
          </button>
        </div>
        {isDragging && (
          <div className="drag-overlay">
            <span>파일을 놓아주세요</span>
          </div>
        )}
      </div>
    );
  };

  renderSummary = () => {
    const { searchResults, totalSearchTime, isProcessing, processingProgress } = this.state;
    if (searchResults.length === 0) return null;

    const totalCount = searchResults.length;
    const matchedCount = searchResults.filter(r => r.status === 'found' || r.status === 'multiple').length;
    const notFoundCount = searchResults.filter(r => r.status === 'not_found').length;
    const pendingCount = searchResults.filter(r => r.status === 'pending').length;

    const totalSavings = searchResults.reduce((sum, r) => {
      if (r.priceDiff && r.priceDiff < 0) {
        return sum + Math.abs(r.priceDiff);
      }
      return sum;
    }, 0);

    return (
      <div className="chemical-summary">
        <div className="summary-item">
          <span className="summary-label">전체</span>
          <span className="summary-value">{totalCount}건</span>
        </div>
        <div className="summary-item matched">
          <span className="summary-label">매칭</span>
          <span className="summary-value">{matchedCount}건</span>
        </div>
        <div className="summary-item not-found">
          <span className="summary-label">미매칭</span>
          <span className="summary-value">{notFoundCount}건</span>
        </div>
        {isProcessing ? (
          <div className="summary-item processing">
            <span className="summary-label">처리중</span>
            <span className="summary-value">{processingProgress}%</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${processingProgress}%` }} />
            </div>
          </div>
        ) : (
          <div className="summary-item">
            <span className="summary-label">처리시간</span>
            <span className="summary-value">{((totalSearchTime || 0) / 1000).toFixed(2)}초</span>
          </div>
        )}
        {totalSavings > 0 && (
          <div className="summary-item savings">
            <span className="summary-label">예상 절감액</span>
            <span className="summary-value">{totalSavings.toLocaleString()}원</span>
          </div>
        )}
        <button
          className="export-btn"
          onClick={this.exportResults}
          disabled={isProcessing || pendingCount > 0}
          data-tip="검색 결과를 CSV 파일로 다운로드합니다. 모든 대체품 정보가 포함됩니다"
          data-for="chemical-tooltip"
        >
          결과 내보내기
        </button>
        <button
          className="clear-btn"
          onClick={this.clearSavedResults}
          disabled={isProcessing}
          data-tip="저장된 모든 검색 결과를 삭제합니다"
          data-for="chemical-tooltip"
        >
          전체 삭제
        </button>
      </div>
    );
  };

  renderResultsTable = () => {
    const { searchResults, isProcessing } = this.state;
    if (searchResults.length === 0) return null;

    return (
      <div className="chemical-results">
        <table className="chemical-table">
          <thead>
            <tr>
              <th className="th-expand" />
              <th className="th-cas">
                CAS 번호
                <span className="info-icon" data-tip="Chemical Abstracts Service 등록 번호로 화학물질 고유 식별자입니다" data-for="chemical-tooltip">?</span>
              </th>
              <th className="th-name">제품명</th>
              <th className="th-brand">브랜드</th>
              <th className="th-current-price">
                기존 가격
                <span className="info-icon" data-tip="업로드한 엑셀에 입력된 현재 구매 가격입니다" data-for="chemical-tooltip">?</span>
              </th>
              <th className="th-best-price">
                최저 가격
                <span className="info-icon" data-tip="동일 CAS 번호 제품 중 가장 낮은 가격입니다" data-for="chemical-tooltip">?</span>
              </th>
              <th className="th-diff">
                가격 비교
                <span className="info-icon" data-tip="기존 가격 대비 절감/증가 금액입니다. 마이너스는 절감을 의미합니다" data-for="chemical-tooltip">?</span>
              </th>
              <th className="th-confidence">
                정확도
                <span className="info-icon" data-tip="CAS 번호 일치 정확도입니다. 100%는 정확히 일치함을 의미합니다" data-for="chemical-tooltip">?</span>
              </th>
              <th className="th-substitutes">
                대체품
                <span className="info-icon" data-tip="동일 CAS 번호를 가진 다른 공급업체 제품 수입니다" data-for="chemical-tooltip">?</span>
              </th>
              <th className="th-delete" />
            </tr>
          </thead>
          <tbody>
            {searchResults.map((result, idx) => (
              <React.Fragment key={result.rowIndex}>
                <tr className={`result-row status-${result.status}`}>
                  <td className="td-expand">
                    {result.matchedProducts.length > 0 && (
                      <button
                        className={`expand-btn ${result.isExpanded ? 'expanded' : ''}`}
                        onClick={() => this.toggleRowExpansion(idx)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                    )}
                  </td>
                  <td className="td-cas">{result.casNumber}</td>
                  <td className="td-name">{result.productName || '-'}</td>
                  <td className="td-brand">{result.brand || '-'}</td>
                  <td className="td-current-price">{formatPrice(result.currentPrice)}</td>
                  <td className="td-best-price">
                    {result.bestMatch ? formatPrice(result.bestMatch.min_price) : '-'}
                  </td>
                  <td className={`td-diff ${formatPriceDiff(result.priceDiff, result.priceDiffPercent).className}`}>
                    {formatPriceDiff(result.priceDiff, result.priceDiffPercent).text}
                  </td>
                  <td className="td-confidence">
                    {result.status === 'pending' ? (
                      <span className="loading-dot" />
                    ) : result.status === 'not_found' ? (
                      '-'
                    ) : (
                      `${result.confidence}%`
                    )}
                  </td>
                  <td className="td-substitutes">
                    {result.matchedProducts.length > 0 ? (
                      <span className="substitute-count">{result.matchedProducts.length}개</span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="td-delete">
                    <button
                      className="delete-row-btn"
                      onClick={() => this.handleDeleteRow(idx)}
                      title="삭제"
                    >
                      ×
                    </button>
                  </td>
                </tr>
                {result.isExpanded && result.matchedProducts.length > 0 && (
                  <tr className="expanded-row">
                    <td colSpan={10}>
                      <div className="substitutes-list">
                        <h4>대체품 목록 ({result.matchedProducts.length}개)</h4>
                        {result.matchedProducts.map((product, pIdx) => (
                          <div key={product.id} className="product-card">
                            <div className="product-header">
                              <a
                                href={`/@${product.username}/products/${product.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="product-title"
                              >
                                {product.title || '-'}
                              </a>
                              <span className="product-brand">{product.brand || '-'}</span>
                              <span className="product-price-range">
                                {formatPrice(product.min_price)}
                                {product.max_price !== product.min_price && ` ~ ${formatPrice(product.max_price)}`}
                              </span>
                              {pIdx === 0 && <span className="best-badge">최저가</span>}
                            </div>
                            {product.product_prices && product.product_prices.length > 0 && (
                              <table className="items-table">
                                <thead>
                                  <tr>
                                    <th>CAS Number</th>
                                    <th>Part Number</th>
                                    <th>브랜드</th>
                                    <th>설명</th>
                                    <th>단위</th>
                                    <th>가격</th>
                                    <th>액션</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {product.product_prices.map(price => (
                                    <tr key={price.id}>
                                      <td className="td-cas">{price.cas_number || '-'}</td>
                                      <td>{price.part_number || '-'}</td>
                                      <td>{price.brand || '-'}</td>
                                      <td className="td-description">{price.description || '-'}</td>
                                      <td>{price.unit || '-'}</td>
                                      <td className="td-price">{formatPrice(price.price)}</td>
                                      <td>
                                        <button
                                          className="chemical-cart-btn"
                                          onClick={e => {
                                            e.stopPropagation();
                                            this.handleAddToCart(product, price);
                                          }}
                                          data-tip="이 상품을 장바구니에 담습니다"
                                          data-for="chemical-tooltip"
                                        >
                                          장바구니
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  render() {
    const { searchResults, error } = this.state;
    const hasResults = searchResults.length > 0;

    const categoryTags = {
      '공급이슈': [
        { name: 'Methanol', cas: '67-56-1' },
        { name: 'Helium', cas: '7440-59-7' },
        { name: 'Bromine', cas: '7726-95-6' },
        { name: 'Urea', cas: '57-13-6' },
        { name: 'Acetonitrile', cas: '75-05-8' },
        { name: 'Ammonia', cas: '7664-41-7' },
        { name: 'Naphtha', cas: '8030-30-6' },
      ],
      '유기화학': [
        { name: 'DCM', cas: '75-09-2' },
        { name: 'THF', cas: '109-99-9' },
        { name: 'DMF', cas: '68-12-2' },
        { name: 'Toluene', cas: '108-88-3' },
        { name: 'Hexane', cas: '110-54-3' },
        { name: 'EtOAc', cas: '141-78-6' },
        { name: 'DMSO', cas: '67-68-5' },
        { name: 'Chloroform', cas: '67-66-3' },
        { name: 'Acetone', cas: '67-64-1' },
      ],
      '생명과학': [
        { name: 'PBS', cas: '7758-11-4' },
        { name: 'EDTA', cas: '60-00-4' },
        { name: 'Tris Base', cas: '77-86-1' },
        { name: 'SDS', cas: '151-21-3' },
        { name: 'Glycerol', cas: '56-81-5' },
        { name: 'Acrylamide', cas: '79-06-1' },
        { name: 'DTT', cas: '3483-12-3' },
        { name: 'BSA', cas: '9048-46-8' },
      ],
      '분석화학': [
        { name: 'ACN HPLC', cas: '75-05-8' },
        { name: 'MeOH HPLC', cas: '67-56-1' },
        { name: 'Formic Acid', cas: '64-18-6' },
        { name: 'TFA', cas: '76-05-1' },
        { name: 'KBr', cas: '7758-02-3' },
        { name: 'H2SO4', cas: '7664-93-9' },
        { name: 'HCl', cas: '7647-01-0' },
        { name: 'HNO3', cas: '7697-37-2' },
        { name: 'NaOH', cas: '1310-73-2' },
      ],
      '반도체/소재': [
        { name: 'IPA', cas: '67-63-0' },
        { name: 'H2O2 30%', cas: '7722-84-1' },
        { name: 'HF', cas: '7664-39-3' },
        { name: 'NMP', cas: '872-50-4' },
        { name: 'PGMEA', cas: '108-65-6' },
        { name: 'Helium', cas: '7440-59-7' },
        { name: 'BOE', cas: '7664-39-3' },
        { name: 'TMAH', cas: '75-59-2' },
      ],
      '제조/산업': [
        { name: 'MEK', cas: '78-93-3' },
        { name: 'Xylene', cas: '1330-20-7' },
        { name: 'Styrene', cas: '100-42-5' },
        { name: 'Cyclohexane', cas: '110-82-7' },
        { name: 'n-Butanol', cas: '71-36-3' },
        { name: 'DEG', cas: '111-46-6' },
        { name: 'EG', cas: '107-21-1' },
        { name: 'Propylene Glycol', cas: '57-55-6' },
        { name: 'Ethyl Acetate', cas: '141-78-6' },
      ],
      '환경/수질': [
        { name: 'KMnO4', cas: '7722-64-7' },
        { name: 'Na2S2O3', cas: '7772-98-7' },
        { name: 'NH4Cl', cas: '12125-02-9' },
        { name: 'Oxalic Acid', cas: '144-62-7' },
        { name: 'FeSO4', cas: '7720-78-7' },
        { name: 'NaOCl', cas: '7681-52-9' },
      ],
      '식품/농업': [
        { name: 'Ethanol', cas: '64-17-5' },
        { name: 'Acetic Acid', cas: '64-19-7' },
        { name: 'Citric Acid', cas: '77-92-9' },
        { name: 'Urea', cas: '57-13-6' },
        { name: 'Ascorbic Acid', cas: '50-81-7' },
        { name: 'NaCl', cas: '7647-14-5' },
      ],
    };

    return (
      <PageTemplate>
        <header>
          <MainHeadContainer />
          <MainMobileHeadContainer />
        </header>
        <div className="chemical-hero">
          <div className="hero-molecules" aria-hidden="true">
            <span className="mol" style={{ right: '6%', top: '8%', animationDelay: '0s', animationDuration: '20s' }}>C₆H₁₂O₆</span>
            <span className="mol" style={{ right: '18%', top: '22%', animationDelay: '3s', animationDuration: '17s' }}>CH₃COOH</span>
            <span className="mol" style={{ right: '4%', top: '42%', animationDelay: '1.5s', animationDuration: '22s' }}>NaHCO₃</span>
            <span className="mol" style={{ right: '24%', top: '55%', animationDelay: '5s', animationDuration: '19s' }}>C₂H₅OH</span>
            <span className="mol" style={{ right: '10%', top: '70%', animationDelay: '2s', animationDuration: '24s' }}>H₂SO₄</span>
            <span className="mol" style={{ right: '32%', top: '14%', animationDelay: '4s', animationDuration: '18s' }}>NH₄Cl</span>
            <span className="mol" style={{ right: '28%', top: '80%', animationDelay: '6s', animationDuration: '21s' }}>CH₃OH</span>
            <span className="mol" style={{ right: '40%', top: '6%', animationDelay: '1s', animationDuration: '16s' }}>KMnO₄</span>
          </div>
          <div className="hero-content">
            <span className="hero-badge">B2B CHEMICAL PLATFORM · SINCE 2017</span>
            <h1 className="hero-title">
              전세계 시약·장비<br />
              <span className="highlight">원스톱 마켓</span>
            </h1>
            <p className="hero-subtitle">35개+ 브랜드 · 383만+ 품목 · 당일 출고 가능</p>

            {this.renderSearchArea()}

            <div className="category-tags">
              {Object.entries(categoryTags).map(([category, tags]) => (
                <div key={category} className="tag-row">
                  <span className="tag-category">{category}</span>
                  <div className="tags">
                    {tags.map(tag => (
                      <button
                        key={tag.cas}
                        className={`tag ${category === '공급이슈' ? 'hot' : ''}`}
                        data-cas={tag.cas}
                        onClick={() => this.setState({ searchQuery: tag.cas }, () => {
                          const form = document.querySelector('.search-form');
                          if (form) form.dispatchEvent(new Event('submit', { bubbles: true }));
                        })}
                      >
                        #{tag.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="chemical-page">
          <div className="chemical-container">
            {error && (
              <div className="chemical-error">
                <span>{error}</span>
                <button onClick={() => this.setState({ error: null })}>×</button>
              </div>
            )}

            {hasResults && (
              <React.Fragment>
                {this.renderSummary()}
                {this.renderResultsTable()}
              </React.Fragment>
            )}
          </div>
        </div>
        <ReactTooltip
          id="chemical-tooltip"
          place="top"
          effect="solid"
          className="chemical-tooltip"
          delayShow={200}
        />
      </PageTemplate>
    );
  }
}

export default Chemical;



// WEBPACK FOOTER //
// ./src/pages/Chemical.js